import { effect, inject, Injectable, isDevMode, NgZone, untracked } from '@angular/core';

import { SkWhatsNewService } from '@mercadoni/stark-ui';

import { environment } from '../../environments/environment';
import type { FuryRelease, UserRole } from '../core/interfaces';
import { normalizeClientId } from '../shared/utils/client-id.util';
import { FirestoreWhatsNewStorage } from './firestore-whats-new-storage.service';
import { matchesAudience } from './release-visibility';
import { WHATS_NEW_EVENTS } from './whats-new-events';
import { WhatsNewVisibilityService } from './whats-new-visibility.service';

/** Options of `furyWhatsNew.preview()`. */
export interface WhatsNewPreviewOptions {
  /** Preview as a user with this role: only releases whose audience matches are offered. */
  asRole?: UserRole;
  /** Preview as an account of this tenant (combined with `asRole` when both are given). */
  asClient?: string;
  /** Play the intro even though it already ran this session. Default `true`. */
  intro?: boolean;
}

/** Console API exposed as `window.furyWhatsNew` in development builds and PR preview channels. */
export interface FuryWhatsNewConsoleApi {
  /** Opens the modal with `eventId`'s look (default: `default`) without persisting seen state. */
  preview(eventId?: string, options?: WhatsNewPreviewOptions): void;
  /** Lists the registered event ids. */
  themes(): string[];
  /** Deletes your onboarding state so the next load is a first visit. Development environment only. */
  reset(): Promise<void>;
}

declare global {
  interface Window {
    furyWhatsNew?: FuryWhatsNewConsoleApi;
  }
}

/** Firebase Hosting preview channels for PRs are served from `<site>--pr-<n>-<hash>.web.app`. */
const PR_PREVIEW_HOST = /--pr-\d+-/;

/**
 * Whether `window.furyWhatsNew` should exist: local dev builds and PR preview channels (which are
 * production builds, so `isDevMode()` is false there). The live site and staging never expose it.
 */
export function isWhatsNewToolingEnabled(
  hostname: string | undefined = globalThis.location?.hostname,
  devMode: boolean = isDevMode(),
): boolean {
  return devMode || (!!hostname && PR_PREVIEW_HOST.test(hostname));
}

/**
 * Registers `window.furyWhatsNew` so the modal, its animation and event looks can be exercised on
 * demand. Only instantiated when `isWhatsNewToolingEnabled()` (see app.config.ts), so the live site
 * never exposes it. A preview swaps the engine's catalog and suspends seen-state writes for its
 * duration; `reset()` additionally requires a non-production environment, so PR previews (built
 * against production data) can preview but never delete anything.
 */
@Injectable({ providedIn: 'root' })
export class WhatsNewDevService {
  private readonly engine = inject(SkWhatsNewService);
  private readonly visibility = inject(WhatsNewVisibilityService);
  private readonly storage = inject(FirestoreWhatsNewStorage);
  private readonly zone = inject(NgZone);

  private previewing = false;

  constructor() {
    if (typeof window === 'undefined' || !isWhatsNewToolingEnabled()) return;

    // Console calls run outside Angular's zone; re-enter it so the overlay renders and its
    // timers are tracked like any in-app interaction.
    window.furyWhatsNew = {
      preview: (eventId, options) => this.zone.run(() => this.preview(eventId, options)),
      themes: () => Object.keys(WHATS_NEW_EVENTS),
      reset: () => this.reset(),
    };

    // Leaving the modal ends the preview: restore the user's real catalog and writes.
    effect(() => {
      const open = this.engine.isOpen();
      untracked(() => {
        if (!open && this.previewing) this.endPreview();
      });
    });
  }

  private preview(eventId: string = 'default', options: WhatsNewPreviewOptions = {}): void {
    const registered = Object.keys(WHATS_NEW_EVENTS);
    if (!registered.includes(eventId)) {
      console.warn(`[furyWhatsNew] Event "${eventId}" is not registered. Registered: ${registered.join(', ')}`);
      return;
    }

    const all = this.visibility.allReleases();
    if (!all) {
      console.warn('[furyWhatsNew] The release catalog is still loading; try again in a moment.');
      return;
    }

    const restricted = options.asRole !== undefined || options.asClient !== undefined;
    const viewer = {
      roles: options.asRole ? [options.asRole] : this.visibility.viewer().roles,
      clientId: options.asClient ? normalizeClientId(options.asClient) : null,
    };
    const releases = (restricted ? all.filter((release) => matchesAudience(release, viewer)) : all).map(
      (release): FuryRelease => ({ ...release, event: eventId === 'default' ? release.event : eventId }),
    );

    if (releases.length === 0) {
      console.info('[furyWhatsNew] No release is visible for that role/client; nothing to preview.');
      return;
    }

    this.previewing = true;
    this.storage.writesSuspended.set(true);
    this.visibility.beginPreview(releases);
    this.visibility.forcedIntro.set(options.intro ?? true);
    this.engine.open(releases[0].id);

    console.info(`[furyWhatsNew] Previewing "${releases[0].title}" with the "${eventId}" look.`);
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      console.warn(
        '[furyWhatsNew] Your system has "reduce motion" enabled, so the intro and card animations are skipped on purpose.',
      );
    }
  }

  private endPreview(): void {
    this.previewing = false;
    this.storage.writesSuspended.set(false);
    this.visibility.endPreview();
  }

  private async reset(): Promise<void> {
    if (environment.production) {
      console.warn('[furyWhatsNew] reset() is only available in the development environment.');
      return;
    }
    await this.storage.clearState();
    console.info('[furyWhatsNew] Onboarding state deleted. Reload to run the first-visit flow.');
  }
}
